import { api } from '@/api'
import store from '@/store'
import { deepFind } from '@/utils/find'
import { string2Epoch, epoch2fullDate, epoch2date, isToday } from '@/utils/time'

export default {
  // 保存しているデータとタイマーをクリアする。
  clearData ({ commit, dispatch }) {
    dispatch('clearTimer')
    dispatch('clearFields')
    commit('setResult', [])
  },
  // 検索条件をクリアする。
  clearFields ({ commit }) {
    commit('setField', {
      area: null,
      name: '',
      tel: '',
      serialNo: ''
    })
  },
  // 検索条件が設定されていれば顧客検索リクエストを発行し結果をVuexに保存する。
  async search ({ state, rootState, commit, dispatch }) {
    const { field } = state
    const query = {}

    Object.keys(field).forEach(prop => {
      if (field[prop]) query[prop] = field[prop]
    })
    if (!Object.keys(query).length) return

    commit('setLoading', true)
    commit('setResult', [])

    try {
      const response = await api.searchCustomers({
        groupId: query.area,
        customerCode: query.customerCode,
        name: query.name,
        tel: query.tel
      })

      let customers = response.customers
      customers = arrangeCustomerData(customers, rootState.groups.group)

      dispatch('startTimer')
      commit('setResult', customers)
    } finally {
      commit('setLoading', false)
    }
  },

  // 「データ取得時間」を現在時刻にセットし、１分ごとに「現在時刻」をアップデートするインターバルを開始する。
  startTimer ({ state, commit, dispatch }) {
    // 既にタイマーが存在していればクリアする。
    if (state.intervalId) {
      dispatch('clearTimer')
    }
    commit('setCurrentTimestamp', new Date().getTime())
    commit('setFetchedTimestamp', new Date().getTime())
    commit('setIntervalId', setInterval(() => {
      commit('setCurrentTimestamp', new Date().getTime())
    }, 1000 * 60))
  },
  // タイマーをクリアする。
  clearTimer ({ state, commit }) {
    clearInterval(state.intervalId)
    commit('setIntervalId', null)
    commit('setCurrentTimestamp', null)
    commit('setFetchedTimestamp', null)
  },

  async createCustomer ({ dispatch }, request) {
    const { id } = await api.createCustomer(request)
    dispatch('setListCustomers', request.deliveryGroupId)
    return id
  },
  async updateCustomer ({ rootState, commit, state, dispatch }, { data, id }) {
    await api.updateCustomer(data, id)

    const index = state.result.findIndex(c => c.id === id)

    // Update the area info of local customer's data only if it was changed
    if (state.result[index].area !== data.deliveryGroupId) {
      const group = deepFind([rootState?.groups?.group], g => g.id === data.deliveryGroupId)
      data.areaName = group?.name
      data.area = data.deliveryGroupId
    }
    // deliveryGroupId is only used for api call (in user data it is called 'area')
    delete data.deliveryGroupId

    commit('updateCustomer', { data, index })

    dispatch('setListCustomers', state.result[index].area)
    return true
  },
  async deleteCustomer ({ commit, dispatch }, customer) {
    await api.deleteCustomer(customer.id)
    commit('app/setGlobalSnackBarText', `顧客（${customer.name}さん）を削除しました。`, { root: true })
    dispatch('initListCustomers', customer.area)
    dispatch('search')
  },

  async createCustomerTank ({ commit, state, dispatch }, data) {
    const response = await api.createCustomerTank(data.reqData, data.customerId)

    let customerIndex = state.result.findIndex(e => e.id === data.customerId)

    if (response !== undefined) {
      if (typeof (response.no) === 'number' && response.result === 'OK') {
        data.tankId = response.no
      }
    }

    let commitData = {
      data,
      customerIndex
    }
    commit('createCustomerTank', commitData)
    dispatch('setListCustomers', state.result[customerIndex].area)

    return data.tankId
  },
  async updateCustomerTank ({ commit, state, dispatch }, { reqData, customerId, tankId }) {
    await api.updateCustomerTank(reqData, customerId, tankId)

    const [customerIndex, tankIndex] = getIndexes(state.result, customerId, tankId)

    let commitData = {
      reqData,
      customerIndex,
      tankIndex
    }
    commit('updateCustomerTank', commitData)
    dispatch('setListCustomers', state.result[customerIndex].area)

    return true
  },
  async deleteCustomerTank ({ commit, state, dispatch }, { customerId, tankId }) {
    await api.deleteCustomerTank(customerId, tankId)
    const [customerIndex, tankIndex] = getIndexes(state.result, customerId, tankId)
    commit('deleteOneTank', { customerIndex, tankIndex })
    dispatch('setListCustomers', state.result[customerIndex].area)
    return true
  },

  async updateSensor ({ commit, state, dispatch }, { body, customerId, tankId, serial }) {
    await api.replaceSensor(body, customerId, tankId)

    const { to, from } = serial
    const [customerIndex, tankIndex] = getIndexes(state.result, customerId, tankId)

    let message
    if (!to) { // Delete
      commit('deleteSensor', { serialNo: from, customerIndex, tankIndex })
      message = `センサー（シリアルナンバー：${from}）を撤去しました。`
    } else if (!from) { // Add
      commit('addSensor', { serialNo: to, customerIndex, tankIndex })
      message = `センサー（シリアルナンバー：${to}）を設置しました。`
    } else { // Change
      commit('changeSensor', { from, to, customerIndex, tankIndex })
      message = `センサーを交換しました。（${from} → ${to}）`
    }
    dispatch('setListCustomers', state.result[customerIndex].area)

    return message
  },

  async setDeliveryDate ({ commit, state, dispatch }, { customerId, deliveryDate, reason, oldDate = null }) {
    let operation, promise
    const date = string2Epoch(deliveryDate)
    if (oldDate) {
      oldDate = string2Epoch(oldDate)
      promise = api.updateCustomerSupplyAppointments({ date, reason }, customerId, oldDate)
      operation = '変更'
    } else {
      promise = api.createCustomerSupplyAppointments({ date, reason }, customerId)
      operation = '登録'
    }

    const index = state.result.findIndex(e => e.id === customerId)
    const name = store.state.app.user.name
    const { date: tooltipDate, status } = deliveryStatusCalculator(date)
    commit('updateCustomer', {
      index,
      data: {
        deliveryDate: {
          ISOdate: deliveryDate, reason, name, date: tooltipDate, status
        }
      }
    })

    await promise
    dispatch('initListCustomers', state.result[index].area)

    return operation
  },
  async clearDeliveryDate ({ commit, state, rootState, dispatch }, { customerId, oldDate }) {
    const date = string2Epoch(oldDate)
    const promise = api.deleteCustomerSupplyAppointments(customerId, date)

    const index = state.result.findIndex(e => e.id === customerId)
    commit('updateCustomer', {
      index,
      data: {
        deliveryDate: {
          ISOdate: null, status: null, date: null, name: '', reason: ''
        }
      }
    })

    await promise
    dispatch('initListCustomers', state.result[index].area)
  },

  async sendCustomerSupply ({ state, dispatch }, { body, id, initList }) {
    await api.createCustomerSupply(body, id)

    if (initList) {
      const area = state.result.find(e => e.id === id).area
      dispatch('initListCustomers', area)
    }
  },

  // CRUDした顧客のエリアがリスト作成のエリアと一致する場合、リスト作成の顧客を更新する。
  setListCustomers ({ rootState, dispatch }, area) {
    if (rootState.list.area === area) {
      dispatch('list/setCustomers', { init: false }, { root: true })
    }
  },
  // CRUDした顧客のエリアがリスト作成のエリアと一致する場合、リスト作成の顧客を更新し選択リストも更新する。
  initListCustomers ({ rootState, dispatch }, area) {
    if (rootState.list.area === area) {
      dispatch('list/setCustomers', { init: true }, { root: true })
    }
  },

  async updateCsvProgress ({ commit }, { groupId, processId }) {
    (async function checkPeriodically () {
      const { status, total, done } = await api.getUploadStatus(groupId, processId)

      const progress = status === 'failed'
        ? -1
        : Math.floor(done / total * 100)

      commit('setCsvUploadStatus', progress)

      if (status === 'in process') {
        setTimeout(checkPeriodically, 500)
      }
    })()
  }
}

/**
 * APIから得た顧客データを使用に即した形に整えて返す。
 * @param {Array} customers
 * @param {object} group
 * @return {Array}
 */
const arrangeCustomerData = (customers, group) => {
  // グループ下に存在するエリア名を全て取得しMAPにする。
  const areaId2name = new Map()
  const queue = [group]

  while (queue.length > 0) {
    const g = queue.pop()
    if (g.type === 'area') {
      areaId2name.set(g.id, g.name)
    }
    queue.push(...g.children)
  }

  return customers.map(c => ({
    original: c,
    id: c.id,
    code: c.code,
    name: c.name || '',
    area: c.deliveryGroupId,
    areaName: areaId2name.get(c.deliveryGroupId),
    address: c.address || '',
    tel: c.tel || '',
    remarks: c.remarks || '',
    deliveryDate: setDeliveryDate(c.latestAppointment),
    tanks: c.tanks.map(t => {
      // 顧客新規登録によるローカル更新の場合タンクidがないため、1からアサインする。
      let idCount = 1
      const assignId = () => idCount++

      return {
        id: t.id || assignId(),
        tankType: t.tankType,
        serialNo: t.serialNo || '',
        oilLevel: t.oilLevel,
        oilVolume: t.oilVolume,
        oilMaxVolume: t.oilMaxVolume,
        legalMaxVolume: t.tankMaxVolume,
        volumeThreshold: t.volumeThreshold,
        volumeMargin: t.volumeMargin,
        volumeUpdatedAt: t.volumeUpdatedAt,
        sensorInstalledAt: t.sensorInstalledAt,
        deliveryFrequency: t.deliveryFrequency
      }
    }),
    sensors: c.tanks.map(t => t.serialNo).filter(s => s != null)
  }))
}

/**
 * 与えられた配列から指定した顧客IDとタンクIDのインデックスをそれぞれ返す。
 * @param {Array} arr 検索対象
 * @param {Number} customerId
 * @param {Number} tankId
 * @return {[Number, Number]} [顧客ID、タンクID]
 */
const getIndexes = (arr, customerId, tankId) => {
  const customerIndex = arr.findIndex(e => e.id === customerId)
  const tankIndex = arr[customerIndex].tanks.findIndex(e => e.id === tankId)
  return [customerIndex, tankIndex]
}

/**
 *
 * @param {{ date: Number, name: String, reason: String }} deliveryDate
 * @return {{ date: String, name: String, reason: String } | {}}
 */
const setDeliveryDate = ({ time, userName, reason } = {}) => {
  if (!time) return {}
  const { date, status } = deliveryStatusCalculator(time)
  return {
    ISOdate: epoch2fullDate(time, '-'),
    date,
    status,
    name: userName,
    reason
  }
}

const deliveryStatusCalculator = time => {
  let date, status
  if (isToday(time)) {
    date = '今日'
    status = 0
  } else {
    date = epoch2date(time)
    status = time > new Date().getTime() / 1000
      ? 1
      : -1
  }
  return { date, status }
}
